import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router';
import { firebaseAuth } from '@/lib/firebase';
import { loginConnect } from '@/features/auth/api';
import { useAuthStore } from '@/features/auth/store';
import { isAdminRole, NotAuthorizedError } from '@/features/auth/constants';
export function useCurrentUser() {
    return useAuthStore((s) => s.user);
}
export function useLogin() {
    const navigate = useNavigate();
    const setAuth = useAuthStore((s) => s.setAuth);
    const clearAuth = useAuthStore((s) => s.clearAuth);
    return useMutation({
        mutationFn: async (signInFn) => {
            const credential = await signInFn();
            const idToken = await credential.user.getIdToken();
            const response = await loginConnect(idToken);
            // Admin gate (instant UX; backend 401/403 remains authoritative). Reject
            // non-admins: clear any prior session and surface "not authorized".
            if (!isAdminRole(response.data.user.role)) {
                clearAuth();
                throw new NotAuthorizedError();
            }
            return { response };
        },
        onSuccess: ({ response }) => {
            const { accessToken, refreshToken, user } = response.data;
            setAuth({ accessToken, refreshToken }, {
                // API returns _id and fullname — normalize to uid and displayName
                uid: user._id,
                email: user.email,
                displayName: user.fullname,
                avatar: user.avatar,
                username: user.username,
                role: user.role,
                membership: user.membership,
            });
            // Admin app has no workspaces — land on the dashboard at '/'.
            void navigate('/');
        },
    });
}
export function useLogout() {
    const navigate = useNavigate();
    const clearAuth = useAuthStore((s) => s.clearAuth);
    const queryClient = useQueryClient();
    // Decoupled from workspaces: the admin app has no workspace store to clear.
    return useCallback(async () => {
        await signOut(firebaseAuth);
        clearAuth();
        queryClient.clear();
        void navigate('/login');
    }, [navigate, clearAuth, queryClient]);
}
